import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RefreshCw, CheckCircle2, Eye, ChevronDown, List, LayoutGrid, Inbox } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getEtlDocuments, updateEtlDocument, fetchImportPdf, checkEtlInbox } from '../../utils/api';
import { detectFileViewerType, getFileNameFromPath } from '../../utils/fileViewer';
import { ConfigToolbar, FilterChip, ToolbarButton, ConfigEmptyState } from '../ConfigPages';
import { Pagination } from './FileTrackingShared';
import { EtlLoading, EtlBanner } from '../EtlPages';
import FileViewerModal from '../FileViewerModal';
import DocumentsGrid from './DocumentsGrid';
import SendDocumentToFeedModal from './SendDocumentToFeedModal';

// Documents tab — every file a property emails to reports+{hotelId}@lodgeic.io
// including both configured report feeds and unmatched captured attachments.

const CATEGORY_LABEL = {
  ledger: 'Ledger',
  audit: 'Audit',
  market_segment: 'Market Segment',
  accounts_receivable: 'Accounts Receivable',
  accounts_payable: 'Accounts Payable',
  financial_statement: 'Financial Statement',
  forecast: 'Forecast',
  labor: 'Labor & Payroll',
  tax: 'Tax',
  invoice: 'Invoice',
  statistics: 'Statistics',
  contract: 'Contract',
  correspondence: 'Correspondence',
  bank_statement: 'Bank Statement',
  other: 'Other',
  unclassified: 'Needs Classification',
};

// Only `unclassified` earns a colour — it is the one category that asks the
// reader to do something.
// One page of documents. The catalog spans two large tables, so the list is
// served a page at a time rather than shipped whole and filtered in the browser.
const PAGE_SIZE = 100;

const CATEGORY_TONE = { unclassified: ' cp-warn', other: '' };

function CategoryPill({ category }) {
  return (
    <span className={`cp-tag${CATEGORY_TONE[category] ?? ' cp-info'}`}>
      {CATEGORY_LABEL[category] || category}
    </span>
  );
}

// 'etl' = matched by a configured report feed; 'captured' = auto-classified
// from an otherwise-unrecognized email attachment.
function SourceBadge({ source }) {
  if (source !== 'etl') return null;
  return (
    <span className="cp-tag" title="Part of a configured feed — its processing state is on the Status tab">
      Report feed
    </span>
  );
}

// The email's real subject line, not the classifier's friendly name — feeds
// match on the subject or file name, so those are what this row must show.
// Older rows saved no subject and fall back to the friendly name.
function documentTitle(doc) {
  return doc.emailSubject || doc.displayName || doc.originalFileName;
}

function formatReceived(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsSection({ currentUser, accessibleHotelIds, locations, scopeReady = true }) {
  const loadRequestId = useRef(0);
  const [documents, setDocuments] = useState([]);   // the current page only
  const [total, setTotal] = useState(0);            // full match count
  const [page, setPage] = useState(0);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState(Object.keys(CATEGORY_LABEL));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [viewLayout, setViewLayout] = useState('list'); // 'list' | 'grid'
  const [checkingInbox, setCheckingInbox] = useState(false);
  const [feedDocument, setFeedDocument] = useState(null);

  // File viewer state
  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewerFileType, setViewerFileType] = useState('pdf');
  const [viewerFileName, setViewerFileName] = useState('');

  const locationNameById = useMemo(() => {
    const map = new Map();
    (locations || []).forEach((l) => map.set(l.locationId || l.id, l.name));
    return map;
  }, [locations]);

  // 60-day window shared by the list fetch and the grid's date columns.
  const dateRange = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 60);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }, []);

  // Search runs on the server, so it is debounced rather than applied per
  // keystroke, and any change resets to the first page.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);
  useEffect(() => { setPage(0); }, [debouncedSearch, categoryFilter]);

  const loadDocuments = useCallback(async () => {
    // The organization scope resolves in two steps (signed-in org, then the
    // user's full set), so two loads can be in flight at once. Without this
    // guard the earlier, narrower response could land last and blank the list.
    if (!scopeReady) return;
    const requestId = ++loadRequestId.current;
    if (!accessibleHotelIds || accessibleHotelIds.length === 0) {
      setDocuments([]);
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getEtlDocuments({
        hotelIds: accessibleHotelIds,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        category: categoryFilter || undefined,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      if (loadRequestId.current !== requestId) return;
      setDocuments(result.documents || []);
      setTotal(result.total ?? (result.documents || []).length);
      setSummary(result.summary || null);
      if (Array.isArray(result.categories) && result.categories.length > 0) {
        setCategories(result.categories);
      }
    } catch (err) {
      if (loadRequestId.current !== requestId) return;
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
    } finally {
      if (loadRequestId.current === requestId) setLoading(false);
    }
  }, [scopeReady, accessibleHotelIds, categoryFilter, dateRange, debouncedSearch, page]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // The server already applied the search, the category and the page window.
  const filteredDocuments = documents;

  const handleView = async (doc) => {
    try {
      const fileType = detectFileViewerType(doc.blobUrl, 'pdf');
      const blobUrl = await fetchImportPdf(doc.blobUrl);
      setViewerUrl(blobUrl);
      setViewerFileType(fileType);
      setViewerFileName(doc.originalFileName || getFileNameFromPath(doc.blobUrl));
    } catch (err) {
      console.error('Error opening document:', err);
      toast.error('Failed to open document');
    }
  };

  const handleSignOff = async (doc) => {
    setSavingId(doc.id);
    try {
      await updateEtlDocument(doc.id, { signOff: true, updatedBy: currentUser?.email || 'unknown' });
      toast.success('Document signed off');
      setDocuments((docs) => docs.map((d) => (
        d.id === doc.id
          ? { ...d, signedOffBy: currentUser?.email || 'unknown', signedOffAt: new Date().toISOString() }
          : d
      )));
    } catch (err) {
      console.error('Error signing off document:', err);
      toast.error('Failed to sign off document');
    } finally {
      setSavingId(null);
    }
  };

  const handleRecategorize = async (doc, category) => {
    if (!category || category === doc.category) {
      setEditCategoryId(null);
      return;
    }
    setSavingId(doc.id);
    try {
      await updateEtlDocument(doc.id, { category, updatedBy: currentUser?.email || 'unknown' });
      toast.success(`Categorized as ${CATEGORY_LABEL[category] || category} — future files with this name will match automatically`);
      setDocuments((docs) => docs.map((d) => (
        d.id === doc.id ? { ...d, category, classificationSource: 'manual' } : d
      )));
    } catch (err) {
      console.error('Error recategorizing document:', err);
      toast.error('Failed to update category');
    } finally {
      setSavingId(null);
      setEditCategoryId(null);
    }
  };

  // Kick the azfEtl inbox sweep now instead of waiting for the next scheduled
  // tick, then reload so anything it captured shows up in the list.
  const handleCheckInbox = useCallback(async () => {
    if (checkingInbox || !accessibleHotelIds || accessibleHotelIds.length === 0) return;
    setCheckingInbox(true);
    const toastId = toast.loading('Checking inbox for new documents…');
    try {
      const result = await checkEtlInbox(accessibleHotelIds);
      const fileCount = Object.values(result?.hotelsWithFileTypes || {})
        .reduce((n, types) => n + (Array.isArray(types) ? types.length : 0), 0);
      const hotelCount = (result?.processedHotelIds || []).length;
      toast.success(
        hotelCount > 0
          ? `Inbox checked — ${fileCount} file type${fileCount === 1 ? '' : 's'} across ${hotelCount} location${hotelCount === 1 ? '' : 's'}`
          : 'Inbox checked — nothing new',
        { id: toastId },
      );
      await loadDocuments();
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Inbox check failed';
      toast.error(detail, { id: toastId });
    } finally {
      setCheckingInbox(false);
    }
  }, [checkingInbox, accessibleHotelIds, loadDocuments]);

  const categoryChips = useMemo(() => {
    const byCategory = summary?.byCategory || {};
    const present = Object.keys(byCategory).sort((a, b) => (byCategory[b] || 0) - (byCategory[a] || 0));
    return present;
  }, [summary]);

  return (
    <div className="it-frame">
      <ConfigToolbar
        search={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search documents…' }}
        chips={(
          <>
            <FilterChip active={!categoryFilter} onClick={() => setCategoryFilter('')}>All</FilterChip>
            {categoryChips.map((cat) => (
              <FilterChip
                key={cat}
                active={categoryFilter === cat}
                warn={cat === 'unclassified'}
                count={summary?.byCategory?.[cat] || 0}
                onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
              >
                {CATEGORY_LABEL[cat] || cat}
              </FilterChip>
            ))}
          </>
        )}
        meta={total > 0
          ? `${total.toLocaleString()} document${total === 1 ? '' : 's'}${debouncedSearch ? ' matching' : ''}`
          : null}
        actions={(
          <>
            <ToolbarButton icon={List} active={viewLayout === 'list'} onClick={() => setViewLayout('list')} title="List">
              List
            </ToolbarButton>
            <ToolbarButton icon={LayoutGrid} active={viewLayout === 'grid'} onClick={() => setViewLayout('grid')} title="Types by day">
              Grid
            </ToolbarButton>
            <ToolbarButton
              icon={Inbox}
              onClick={handleCheckInbox}
              disabled={checkingInbox || !accessibleHotelIds?.length}
              title="Run the email inbox check now and pull in any new documents"
            >
              {checkingInbox ? 'Checking…' : 'Check for new documents'}
            </ToolbarButton>
            <ToolbarButton iconOnly icon={RefreshCw} onClick={loadDocuments} disabled={loading} aria-label="Refresh" title="Refresh" />
          </>
        )}
      />

      {/* Grid layout — portfolio (locations × days) with drill-in to
          document-types × days per location */}
      {viewLayout === 'grid' && !loading && !error && filteredDocuments.length > 0 && (
        <DocumentsGrid
          documents={filteredDocuments}
          locationNameById={locationNameById}
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
        />
      )}

      {/* List */}
      {(viewLayout === 'list' || loading || error || filteredDocuments.length === 0) && (
      <div className="it-scroll" style={{ '--it-max-h': '62vh' }}>
        {loading && <EtlLoading>Loading documents…</EtlLoading>}
        {!loading && error && (
          <div style={{ padding: 18 }}>
            <EtlBanner
              tone="error"
              title="Could not load documents"
              actions={<ToolbarButton small onClick={loadDocuments}>Try again</ToolbarButton>}
            >
              {error}
            </EtlBanner>
          </div>
        )}
        {!loading && !error && filteredDocuments.length === 0 && (
          <ConfigEmptyState
            title="No documents yet"
            body="Every file emailed to this property's Lodgeic address lands here and is categorised automatically — both configured report feeds and anything else you send."
          />
        )}
        {!loading && !error && filteredDocuments.length > 0 && (
          <table className="it-tbl">
            <thead>
              <tr>
                <th>Location</th>
                <th>Subject / File Name</th>
                <th>Category</th>
                <th>Received</th>
                <th>Sign-off</th>
                <th aria-label="Actions" style={{ width: 190 }} />
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc) => (
                <tr key={doc.id} className="cp-row it-row-hover">
                  <td className="cp-cell">
                    <span className="cp-name">
                      {doc.locationName || locationNameById.get(doc.locationId) || `Hotel ${doc.locationId}`}
                    </span>
                  </td>
                  <td className="cp-cell">
                    <div className="cp-name" title={doc.emailSubject ? `Email subject: ${doc.emailSubject}` : doc.displayName}>
                      {documentTitle(doc)}
                    </div>
                    <div className="cp-code" title={doc.originalFileName}>
                      {doc.originalFileName}{doc.fileSizeBytes ? ` · ${formatSize(doc.fileSizeBytes)}` : ''}
                    </div>
                  </td>
                  <td className="cp-cell">
                    {doc.source === 'etl' ? (
                      <span className="inline-flex items-center gap-1.5">
                        <CategoryPill category={doc.category} />
                        <SourceBadge source={doc.source} />
                      </span>
                    ) : editCategoryId === doc.id ? (
                      <select
                        autoFocus
                        defaultValue={doc.category}
                        disabled={savingId === doc.id}
                        onChange={(e) => handleRecategorize(doc, e.target.value)}
                        onBlur={() => setEditCategoryId(null)}
                        className="cp-input"
                        style={{ height: 28, fontSize: 12 }}
                      >
                        {categories.filter((c) => c !== 'unclassified').map((c) => (
                          <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>
                        ))}
                      </select>
                    ) : (
                      <button
                        onClick={() => setEditCategoryId(doc.id)}
                        title="Change category — the correction applies to future files with this name"
                        className="inline-flex items-center gap-1 group"
                      >
                        <CategoryPill category={doc.category} />
                        <ChevronDown size={12} className="text-gray-300 group-hover:text-gray-500" />
                      </button>
                    )}
                  </td>
                  <td className="cp-cell">
                    <div className="cp-mono">{formatReceived(doc.receivedAt)}</div>
                    {doc.effectiveDate && (
                      <div className="cp-code">for {String(doc.effectiveDate).slice(0, 10)}</div>
                    )}
                  </td>
                  <td className="cp-cell">
                    {doc.signedOffAt ? (
                      <span className="cp-tag cp-ok" title={`Signed off by ${doc.signedOffBy}`}>
                        <CheckCircle2 size={11} /><span style={{ marginLeft: 5 }}>Signed off</span>
                      </span>
                    ) : (
                      <ToolbarButton small onClick={() => handleSignOff(doc)} disabled={savingId === doc.id}>
                        Sign off
                      </ToolbarButton>
                    )}
                  </td>
                  <td className="cp-cell-tight" style={{ textAlign: 'right' }}>
                    <span className="inline-flex items-center gap-1">
                      <ToolbarButton small onClick={() => setFeedDocument(doc)} disabled={!doc.blobUrl}>
                        Send to report feed
                      </ToolbarButton>
                      <ToolbarButton small iconOnly variant="ghost" icon={Eye} onClick={() => handleView(doc)}
                                     title="View document" aria-label={`View ${doc.originalFileName}`} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Mobile card list */}
        {!loading && !error && filteredDocuments.length > 0 && (
          <div className="md:hidden divide-y divide-gray-100">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="px-4 py-3">
                <div className="flex items-start justify-between mb-1">
                  <div className="min-w-0">
                    <div className="text-[13px] text-gray-800 font-semibold truncate">
                      {documentTitle(doc)}
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">{doc.originalFileName}</div>
                    <div className="text-[11px] text-gray-400">
                      {doc.locationName || locationNameById.get(doc.locationId) || `Hotel ${doc.locationId}`}
                    </div>
                  </div>
                  <span className="text-[11px] text-gray-400 flex-shrink-0 ml-2">{formatReceived(doc.receivedAt)}</span>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <CategoryPill category={doc.category} />
                  <SourceBadge source={doc.source} />
                  {doc.signedOffAt && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-green-700">
                      <CheckCircle2 size={12} /> Signed off
                    </span>
                  )}
                  <ToolbarButton small onClick={() => setFeedDocument(doc)} disabled={!doc.blobUrl}>
                    Send to report feed
                  </ToolbarButton>
                  <button
                    onClick={() => handleView(doc)}
                    className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-blue-600"
                  >
                    <Eye size={12} /> View
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {!loading && !error && total > PAGE_SIZE && (
        <Pagination
          currentPage={page + 1}
          totalPages={Math.ceil(total / PAGE_SIZE)}
          totalItems={total}
          itemsPerPage={PAGE_SIZE}
          onPageChange={(next) => setPage(Math.max(0, next - 1))}
        />
      )}

      {feedDocument && (
        <SendDocumentToFeedModal
          document={feedDocument}
          onClose={() => setFeedDocument(null)}
          onSent={() => { setFeedDocument(null); loadDocuments(); }}
        />
      )}

      {viewerUrl && (
        <FileViewerModal
          url={viewerUrl}
          fileType={viewerFileType}
          fileName={viewerFileName}
          onClose={() => {
            if (viewerUrl.startsWith('blob:')) URL.revokeObjectURL(viewerUrl);
            setViewerUrl(null);
          }}
        />
      )}
    </div>
  );
}
